// ── OIDC PKCE flow against Hydra (https://hydra.s16e.de) ─────────────

const AUTH_STORAGE_KEY = "spuerhund:auth";
const PENDING_SIGNED_IN_WORKSPACE_KEY = "spuerhund:pending-signed-in-workspace";
const PKCE_VERIFIER_KEY = "spuerhund:pkce_verifier";
const OIDC_STATE_KEY = "spuerhund:oidc_state";

const OIDC_ISSUER = (
  (import.meta.env.VITE_OIDC_ISSUER as string | undefined) ?? "https://hydra.s16e.de"
).replace(/\/$/, "");

const OIDC_CLIENT_ID = (import.meta.env.VITE_OIDC_CLIENT_ID as string | undefined)
  ?? "9b65ca02-dcb9-4d2a-b80a-3faded2ed2b9";

const OIDC_SCOPE = "openid offline_access profile email";

const API_BASE = (
  (import.meta.env.VITE_SHARED_CONFIG_API_BASE as string | undefined)
  ?? "https://spuerhund-api.cloudeflare-8a5.workers.dev/api"
).replace(/\/$/, "");

const ACCESS_TOKEN_REFRESH_SKEW_MS = 60_000;

export interface AuthUser {
  id: string;
  email: string;
  displayName: string;
  avatarUrl: string | null;
}

export interface StoredAuth {
  accessToken: string;
  refreshToken: string;
  user: AuthUser;
}

// ── Storage ──────────────────────────────────────────────────────────

export function getStoredAuth(): StoredAuth | null {
  try {
    const raw = localStorage.getItem(AUTH_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as StoredAuth;
  } catch {
    return null;
  }
}

export function persistAuth(auth: StoredAuth): void {
  localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(auth));
}

export function clearStoredAuth(): void {
  localStorage.removeItem(AUTH_STORAGE_KEY);
  localStorage.removeItem(PENDING_SIGNED_IN_WORKSPACE_KEY);
  localStorage.removeItem(PKCE_VERIFIER_KEY);
  localStorage.removeItem(OIDC_STATE_KEY);
}

export function getAccessToken(): string | null {
  return getStoredAuth()?.accessToken ?? null;
}

export function getRefreshToken(): string | null {
  return getStoredAuth()?.refreshToken ?? null;
}

// ── JWT helpers ──────────────────────────────────────────────────────

function decodeBase64Url(value: string): string {
  const normalized = value.replace(/-/g, "+").replace(/_/g, "/");
  const padding = normalized.length % 4 === 0 ? "" : "=".repeat(4 - (normalized.length % 4));
  return atob(`${normalized}${padding}`);
}

function readAccessTokenExp(accessToken: string): number | null {
  const payloadPart = accessToken.split(".")[1];
  if (!payloadPart) return null;
  try {
    const payload = JSON.parse(decodeBase64Url(payloadPart)) as { exp?: unknown };
    return typeof payload.exp === "number" ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

export function accessTokenNeedsRefresh(accessToken: string, skewMs = ACCESS_TOKEN_REFRESH_SKEW_MS): boolean {
  const expiresAt = readAccessTokenExp(accessToken);
  if (expiresAt === null) return false;
  return expiresAt <= Date.now() + skewMs;
}

// ── Pending signed-in workspace activation (set before login, read after callback) ──────

export function setPendingSignedInWorkspace(): void {
  localStorage.setItem(PENDING_SIGNED_IN_WORKSPACE_KEY, "1");
}

export function consumePendingSignedInWorkspace(): boolean {
  const pending = localStorage.getItem(PENDING_SIGNED_IN_WORKSPACE_KEY);
  if (pending) localStorage.removeItem(PENDING_SIGNED_IN_WORKSPACE_KEY);
  return !!pending;
}

// ── PKCE helpers ─────────────────────────────────────────────────────

function base64UrlEncode(bytes: Uint8Array): string {
  let str = "";
  for (let i = 0; i < bytes.byteLength; i++) str += String.fromCharCode(bytes[i]!);
  return btoa(str).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function randomString(byteLength = 32): string {
  const bytes = new Uint8Array(byteLength);
  crypto.getRandomValues(bytes);
  return base64UrlEncode(bytes);
}

async function sha256(input: string): Promise<string> {
  const data = new TextEncoder().encode(input);
  const digest = await crypto.subtle.digest("SHA-256", data);
  return base64UrlEncode(new Uint8Array(digest));
}

// ── Login flow ───────────────────────────────────────────────────────

export async function initiateLogin(): Promise<void> {
  const redirectUri = `${window.location.origin}/auth/callback`;
  const verifier = randomString(48);
  const challenge = await sha256(verifier);
  const state = randomString(16);

  localStorage.setItem(PKCE_VERIFIER_KEY, verifier);
  localStorage.setItem(OIDC_STATE_KEY, state);

  const params = new URLSearchParams({
    response_type: "code",
    client_id: OIDC_CLIENT_ID,
    redirect_uri: redirectUri,
    scope: OIDC_SCOPE,
    state,
    code_challenge: challenge,
    code_challenge_method: "S256"
  });

  window.location.href = `${OIDC_ISSUER}/oauth2/auth?${params}`;
}

interface OidcTokenResponse {
  access_token: string;
  refresh_token?: string;
  id_token?: string;
  token_type: string;
  expires_in: number;
  scope?: string;
}

async function fetchTokens(body: URLSearchParams): Promise<OidcTokenResponse> {
  const res = await fetch(`${OIDC_ISSUER}/oauth2/token`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`Token exchange failed (${res.status}): ${text || "no body"}`);
  }
  return (await res.json()) as OidcTokenResponse;
}

async function syncUserToBackend(accessToken: string): Promise<AuthUser> {
  const res = await fetch(`${API_BASE}/account/sync`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`
    }
  });
  if (!res.ok) throw new Error(`/api/account/sync failed (${res.status})`);
  return (await res.json()) as AuthUser;
}

export async function handleAuthCallback(code: string): Promise<StoredAuth> {
  const verifier = localStorage.getItem(PKCE_VERIFIER_KEY);
  if (!verifier) throw new Error("Missing PKCE verifier (login flow not started in this tab)");

  const params = new URLSearchParams(window.location.search);
  const stateParam = params.get("state");
  const expectedState = localStorage.getItem(OIDC_STATE_KEY);
  if (!stateParam || stateParam !== expectedState) throw new Error("OIDC state mismatch");

  const redirectUri = `${window.location.origin}/auth/callback`;
  const tokens = await fetchTokens(new URLSearchParams({
    grant_type: "authorization_code",
    code,
    redirect_uri: redirectUri,
    client_id: OIDC_CLIENT_ID,
    code_verifier: verifier
  }));

  localStorage.removeItem(PKCE_VERIFIER_KEY);
  localStorage.removeItem(OIDC_STATE_KEY);

  if (!tokens.refresh_token) throw new Error("No refresh_token returned (offline_access scope?)");
  const user = await syncUserToBackend(tokens.access_token);

  const auth: StoredAuth = {
    accessToken: tokens.access_token,
    refreshToken: tokens.refresh_token,
    user
  };
  persistAuth(auth);
  return auth;
}

export async function refreshAccessToken(): Promise<StoredAuth> {
  const current = getStoredAuth();
  if (!current?.refreshToken) {
    clearStoredAuth();
    throw new Error("No refresh token");
  }

  let tokens: OidcTokenResponse;
  try {
    tokens = await fetchTokens(new URLSearchParams({
      grant_type: "refresh_token",
      refresh_token: current.refreshToken,
      client_id: OIDC_CLIENT_ID
    }));
  } catch (e) {
    clearStoredAuth();
    throw e instanceof Error ? e : new Error("Refresh failed");
  }

  const auth: StoredAuth = {
    accessToken: tokens.access_token,
    refreshToken: tokens.refresh_token ?? current.refreshToken,
    user: current.user
  };
  persistAuth(auth);
  return auth;
}

export async function ensureFreshStoredAuth(): Promise<StoredAuth> {
  const current = getStoredAuth();
  if (!current?.accessToken) {
    clearStoredAuth();
    throw new Error("Not authenticated");
  }
  if (!accessTokenNeedsRefresh(current.accessToken)) return current;
  return refreshAccessToken();
}

export async function logout(): Promise<void> {
  const auth = getStoredAuth();
  if (auth?.refreshToken) {
    // Hydra revokes refresh tokens via /oauth2/revoke; client_id only (public client)
    await fetch(`${OIDC_ISSUER}/oauth2/revoke`, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: new URLSearchParams({
        token: auth.refreshToken,
        client_id: OIDC_CLIENT_ID
      })
    }).catch(() => { /* best-effort */ });
  }
  clearStoredAuth();
}
